import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

export default function InstallPrompt({ view }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        try {
            // 1. Reeds als app / standalone geopend? Dan nooit tonen
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
            if (isStandalone) return;

            // 2. Reeds weggeklikt in de afgelopen 30 dagen?
            const dismissedAt = localStorage.getItem('pwa_prompt_dismissed_at');
            if (dismissedAt) {
                const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
                if (daysSince < 30) return;
            }

            // 3. iOS Safari detectie
            const ua = window.navigator.userAgent.toLowerCase();
            const isIosDevice = /iphone|ipad|ipod/.test(ua);
            const isSafari = /safari/.test(ua) && !/chrome|crios|fxios/.test(ua);

            if (isIosDevice && isSafari) {
                setIsIos(true);
                setShowPrompt(true);
                return;
            }

            // 4. Android / Chrome installatie-event opvangen
            const handleBeforeInstall = (e) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowPrompt(true);
            };

            window.addEventListener('beforeinstallprompt', handleBeforeInstall);
            return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        } catch (err) {
            // Failsafe bij private browsing storage blokkades
        }
    }, []);

    const handleDismiss = () => {
        setShowPrompt(false);
        try {
            localStorage.setItem('pwa_prompt_dismissed_at', String(Date.now()));
        } catch (e) {}
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setShowPrompt(false);
        }
        setDeferredPrompt(null);
    };

    // Verberg tijdens een training of feestscherm
    if (!showPrompt || view === 'focus' || view === 'celebration') return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-xs z-40 animate-fade-in">
            <div className="bg-zinc-900/95 backdrop-blur-xl border border-orange-500/40 p-4 rounded-2xl shadow-2xl text-white flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <img src="/logo192.png" alt="MyWoD" className="w-10 h-10 rounded-xl drop-shadow-md" />
                        <div>
                            <h4 className="font-black text-sm text-white">Installeer MyWoD</h4>
                            <p className="text-xs text-gray-400">Gebruik als app op je telefoon</p>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-gray-500 hover:text-white p-1 rounded-lg transition-colors"
                        aria-label="Sluiten"
                    >
                        <X size={18} />
                    </button>
                </div>

                {isIos ? (
                    <div className="text-xs text-gray-300 bg-black/40 p-2.5 rounded-xl border border-white/5 flex items-center gap-2">
                        <Share size={16} className="text-orange-500 shrink-0" />
                        <span>Tik op <strong>Deel</strong> en kies <strong>'Zet op beginscherm'</strong>.</span>
                    </div>
                ) : (
                    <button
                        onClick={handleInstallClick}
                        className="w-full py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white text-xs font-black rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-wider shadow-lg"
                    >
                        <Download size={15} /> App Installeren
                    </button>
                )}
            </div>
        </div>
    );
}
